/**
 * Shared game-board atoms for Live Game View and Game Results.
 *
 * The colours come from `shared/ReviewShieldChips.jsx` — the same values the
 * Review Shield Results page draws with. Only two extra tones are added here,
 * for the slices that map does not cover. That file is not modified.
 */
import { Star } from 'lucide-react';
import { TOKEN } from './ReviewShieldChips';

const EXTRA = {
    blue: { bg: 'rgba(59, 157, 255, 0.10)', ring: 'rgba(59, 157, 255, 0.45)', fg: '#3B9DFF' },
    violet: { bg: 'rgba(179, 136, 255, 0.10)', ring: 'rgba(179, 136, 255, 0.45)', fg: '#B388FF' },
    // The game board reads orange where Review Shield reads yellow. Its own
    // token file is left exactly as it is.
    orange: { bg: 'rgba(255, 138, 61, 0.12)', ring: 'rgba(255, 138, 61, 0.50)', fg: '#FF8A3D' },
    fuchsia: { bg: 'rgba(232, 62, 184, 0.12)', ring: 'rgba(232, 62, 184, 0.50)', fg: '#E83EB8' },
};

export const TONES = {
    green: TOKEN.resolved,
    amber: EXTRA.orange,
    grey: TOKEN.no_contact,
    red: TOKEN.sla_overdue,
    blue: EXTRA.blue,
    violet: EXTRA.violet,
    fuchsia: EXTRA.fuchsia,
    // The original Review Shield yellow, kept for "Redeemed tonight" only.
    yellow: TOKEN.awaiting,
};

// The order a donut hands colours out, so two slices are never the same.
export const TONE_CYCLE = ['green', 'blue', 'amber', 'violet', 'red', 'grey'];

export const STATUS_TONE = {
    new: 'blue',
    newly_registered: 'amber',
    registered: 'green',
};

const PILL = 'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 '
    + 'text-[11px] font-medium whitespace-nowrap transition-colors duration-200';

/** A coloured pill. `onClick` makes it a filter button; otherwise it is a label. */
export const GamePill = ({ tone, label, count, selected, onClick, testId, title }) => {
    const style = { background: tone.bg, borderColor: tone.ring, color: tone.fg };
    const classes = `${PILL} ${onClick ? 'cursor-pointer hover:brightness-125' : ''} `
        + `${selected ? 'ring-2 ring-offset-1 ring-offset-background' : ''}`;
    if (!onClick) {
        return <span className={classes} style={style} data-testid={testId} title={title}>{label}</span>;
    }
    return (
        <button type="button" onClick={onClick} className={classes}
                style={{ ...style, ...(selected ? { boxShadow: `0 0 0 2px ${tone.ring}` } : {}) }}
                aria-pressed={!!selected} data-testid={testId} title={title}>
            <span>{label}</span>
            {count !== undefined && <span className="opacity-80">{count}</span>}
        </button>
    );
};

/** New / Newly Registered / Registered, on every player row. */
export const PlayerStatusPill = ({ status, t, testId }) => {
    const key = status || 'new';
    return (
        <GamePill tone={TONES[STATUS_TONE[key] || 'grey']} testId={testId}
                  label={t(`gameBoard.status.${key}`, key)} />
    );
};

/** How many players picked each star, over every row of this event. */
export const starPickCounts = (rows) => {
    const counts = {};
    (rows || []).forEach((r) => {
        const name = (r.star_pick || '').trim();
        if (name) counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
};

/** The star pick, made to stand out: a star, the name, in the board's orange. */
export const StarPickCell = ({ name, count, t, testId }) => {
    if (!name) return <span className="text-muted-foreground text-xs">—</span>;
    const tone = TONES.amber;
    return (
        <span className="inline-flex items-center gap-1.5 font-semibold whitespace-nowrap"
              style={{ color: tone.fg }} data-testid={testId}>
            <Star className="w-3.5 h-3.5 shrink-0" fill={tone.fg} strokeWidth={0} />
            <span>{name}</span>
            {count > 1 && (
                <span className="rounded-full px-1.5 text-[11px] font-bold"
                      style={{ background: tone.bg, border: `1px solid ${tone.ring}` }}
                      title={t ? t('gameBoard.starPickChosen', '{{n}} players chose this star', { n: count }) : undefined}>
                    {count}
                </span>
            )}
        </span>
    );
};

/** The count on its own, for the column that sits next to the star pick. */
export const StarPickCount = ({ count, t, testId }) => {
    if (!count) return <span className="text-muted-foreground text-xs">—</span>;
    const tone = TONES.amber;
    return (
        <span className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold"
              style={{ background: tone.bg, border: `1px solid ${tone.ring}`, color: tone.fg }}
              title={t ? t('gameBoard.starPickChosen', '{{n}} players chose this star', { n: count }) : undefined}
              data-testid={testId}>
            {count}
        </span>
    );
};
